
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { FileUploadField } from "./FileUploadField";
import { ReadingFormValues } from "./types";
import { readingService } from "./readingService";
import { ScrollArea } from "@/components/ui/scroll-area";

const CreateReadingDialog = ({ onSuccess }: { onSuccess: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { data: houses } = useQuery({
    queryKey: ['houses'],
    queryFn: readingService.fetchHouses
  });

  const form = useForm<ReadingFormValues>({
    defaultValues: {
      type: "water",
      reading: undefined, // Changed from 0 to undefined for better UX
      month: new Date().toISOString().slice(0, 7)
    }
  });

  const handleSubmit = async (data: ReadingFormValues) => {
    try {
      setIsUploading(true);
      let photoUrl = null;

      if (data.file) {
        photoUrl = await readingService.uploadImage(data.file);
      }

      await readingService.createReading(data, photoUrl);

      toast({
        title: "Éxito",
        description: "Lectura creada exitosamente",
      });

      setIsOpen(false);
      onSuccess();
    } catch (error) {
      console.error('Error creating reading:', error);
      toast({
        title: "Error",
        description: "Error al crear la lectura",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="w-full h-12">
          <Plus className="w-4 h-4 mr-2" />
          Crear lectura
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>Crear lectura</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(90vh-80px)]">
          <div className="p-6 pt-2">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="house"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Casa</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar una casa" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white">
                          {houses?.map((house) => (
                            <SelectItem key={house.id} value={house.id}>
                              {house.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel>Tipo de lectura</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex flex-row space-x-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="water" id="water" />
                            <Label htmlFor="water">Agua</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="electricity" id="electricity" />
                            <Label htmlFor="electricity">Electricidad</Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="reading"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cantidad de lectura</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          {...field}
                          value={field.value === undefined ? '' : field.value}
                          onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                          placeholder="Ingrese la cantidad"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="month"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mes</FormLabel>
                      <FormControl>
                        <Input 
                          type="month" 
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="file"
                  render={() => (
                    <FormItem>
                      <FormLabel>Foto</FormLabel>
                      <FormControl>
                        <FileUploadField form={form} isUploading={isUploading} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={isUploading}>
                  {isUploading ? "Creando..." : "Crear lectura"}
                </Button>
              </form>
            </Form>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CreateReadingDialog;
